import { AppContext } from "context/appContext";
import { useContext, useMemo, useState } from "react";
import { useFetcher, useOutletContext } from "react-router";
import { PermitTransactionStatus, EntityTypes } from "types";
import { PermitFormActionHandler, PermitFormContext } from "./PermitFormLayout";
import { useTranslation } from "react-i18next";
import DetailsHeader from "components/layout/DetailsHeader";
import { Grid, Typography } from "@mui/material";
import AuturaTextField from "components/inputs/AuturaTextField";
import { formatCurrency } from "utils/stringFormatters";
import AuturaCheckbox from "components/inputs/AuturaCheckbox";
import { capitalize } from "utils/stringFormatters";
import { ROUTES } from "router";
import SendIcon from "@mui/icons-material/Send";
import FormFrame from "components/layout/FormFrame";
import { ValidatorForm } from "react-material-ui-form-validator";

const PermitRefunds = () => {
  const { t } = useTranslation();
  const { state } = useContext(AppContext);
  const { permit, displayMode, entity, fetcherKey } =
    useOutletContext<PermitFormContext>();
  const fetcher = useFetcher({ key: fetcherKey });

  const [email, setEmail] = useState<string>(
    permit?.contact?.primaryEmail || "",
  );
  const [cancel, setCancel] = useState<boolean>(true);
  const transaction = permit?.transactions.find(
    (t) => t.status === PermitTransactionStatus.PAID,
  );

  const handleSubmit = () => {
    if (!permit || !transaction) {
      return;
    }
    const contact = { ...permit.contact };
    if (email !== contact?.primaryEmail) {
      contact.primaryEmail = email;
    }
    const entityType =
      displayMode === "property"
        ? EntityTypes.Property
        : EntityTypes.PropertyGroup;
    fetcher.submit(
      {
        body: JSON.stringify(contact),
        tenantID: state.tenant.id,
        permitID: permit.id,
        paymentID: transaction.paymentID,
        permitTypeID: permit.permitType.id,
        entityType,
        entityID: entity.id,
        cancel,
        handler: PermitFormActionHandler.REFUND,
      },
      {
        method: "POST",
        encType: "application/json",
        action: ROUTES.PROPERTY_PERMITS,
      },
    );
  };

  const amountLabel = useMemo(() => {
    if (!transaction) {
      return "";
    }
    const amount = formatCurrency(transaction.data?.details?.amount || 0);
    const paymentMethod = `to ${transaction.data?.paymentMethodDetails?.brand} ending in *${transaction.data?.paymentMethodDetails?.lastFour}`;
    return `${t("amount")}: ${amount} ${paymentMethod}`;
  }, [t, transaction]);

  return (
    <ValidatorForm onSubmit={handleSubmit} instantValidate={false}>
      <FormFrame loading={fetcher.state !== "idle"}>
        <DetailsHeader
          title={t("subheadings.refund", { ns: "permits" })}
          submitLabel={t("buttons.refund", { ns: "permits" })}
          submitButtonIcon={<SendIcon />}
          disableSubmit={fetcher.state !== "idle"}
          onSubmit={handleSubmit}
        />
        <Grid container width={"100%"} spacing={1.5}>
          <Grid size={12} />
          <Grid size={12}>
            <Typography variant="body1">
              {capitalize(t("helpers.refund", { ns: "permits" }))}
            </Typography>
          </Grid>
          <Grid size={12}>
            <Typography variant="body1" fontWeight={"medium"}>
              {capitalize(amountLabel)}
            </Typography>
          </Grid>
          <Grid
            size={{
              xs: 12,
              lg: 3,
            }}
          >
            <AuturaTextField
              label={t("permit.contact.primaryEmail")}
              name={"permit.contact.primaryEmail"}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              lg: 3,
            }}
          >
            <AuturaCheckbox
              sx={{ mt: 1 }}
              label={capitalize(t("helpers.cancel", { ns: "permits" }))}
              checked={cancel}
              setChecked={(value) => setCancel(value)}
            />
          </Grid>
        </Grid>
      </FormFrame>
    </ValidatorForm>
  );
};

export default PermitRefunds;
